/**
 * **记牌器（分析层）** —— 从一份 `PlayerView` 推导"哪些牌还没露面、大概在哪"。
 *
 * ⚠️ 它既给玩家 UI 用、也给 Bot 用，所以**既不放 `core/`（那是规则层）**，
 *    也不放 `bot/`（UI 不该依赖 bot）。放在这里当两者共享的分析模块。
 *
 * 知识分三层（见 `docs/tracker-belief-report.md`）：
 *
 * | 层 | 来源 |
 * | --- | --- |
 * | **定位**（`knownTop`） | Debug 看堆顶 / 亲手插回 |
 * | **区域在牌堆**（`knownInDraw`） | 洗牌之后：还在抽牌堆里、但不知道位次 |
 * | **区域在某手**（`knownInHand`） | 看着某人抽走我已知的牌 / 插回后别人知道在牌堆 |
 *
 * 估计用"未知牌均匀落在隐藏槽位"的一阶矩；稀释的期望是精确的（线性期望）。
 */

import type { CardTitle, PlayerId } from '../core/ids';
import type { CardRegistry } from '../core/pack';
import type { PlayerView } from '../core/project';
import { isLethal } from '../core/card';
import { inPlayDeck } from '../core/setup';
import { cardName } from '../core/cardText';

/** 一张牌的记牌结果 */
export interface TrackerRow {
  readonly title: CardTitle;
  readonly name: string;
  readonly inPlay: number;
  readonly inDiscard: number;
  readonly inYourHand: number;
  /** 定位在抽牌堆里的张数（我知道第 i 张是什么） */
  readonly locatedInDraw: number;
  /** 确定在抽牌堆里、但不知道位次的期望张数 */
  readonly regionInDraw: number;
  /**
   * **还没定位的份额**（会落到"牌堆未知位 + 别人手里未知位"）——
   * 只给下面的"推测"列用。
   *
   * ⚠️ 它**不是**状态栏那个「未知」。状态栏的「未知」恒等于
   * inPlay − inDiscard − inYourHand（见 gameTable.ts），不做这一层扣减 ——
   * 否则"已打出 / 我手里 / 未知"三色加起来对不上在场总数。
   */
  readonly unlocated: number;
  /** 期望在抽牌堆里的张数（定位 + 区域 + 未知摊到的） */
  readonly expectedInDraw: number;
  /** 期望在**每一位**其他玩家手里的张数（含已知在他手里的） */
  readonly expectedInHands: Readonly<Record<string, number>>;
}

export interface TrackerKnown {
  readonly indexFromTop: number;
  readonly title: CardTitle;
  readonly name: string;
}

export interface TrackerPlayer {
  readonly id: PlayerId;
  readonly name: string;
  readonly handCount: number;
  /** 其中"已知在他手里"的期望张数（这些位置不参与未知牌分摊） */
  readonly knownSlots: number;
}

export interface TrackerMemory {
  readonly drawPileCount: number;
  readonly otherHands: number;
  /** 定位 + 区域：我知道在抽牌堆里的总张数（期望） */
  readonly knownInDrawTotal: number;
  /** 未知牌可能落在的位置数：牌堆未知位 + 别人手里未知位 */
  readonly hiddenSlots: number;
  readonly otherPlayers: readonly TrackerPlayer[];
  readonly rows: readonly TrackerRow[];
  readonly knownTop: readonly TrackerKnown[];
}

/** 状态栏那一格：已打出 / 我手里 / 未知（加起来 = 这一型的在场总数） */
export interface TrackerStatusCounts {
  readonly discarded: number;
  readonly inHand: number;
  readonly unknown: number;
}

/**
 * **状态栏那三个数。**
 *
 * ⚠️ 「未知」在这里恒等于 inPlay − inDiscard − inYourHand ——
 * **不扣**定位 / 区域 / 已知手牌（那些只影响"推测"列）。扣了的话三色
 * 加起来就小于在场总数（用户报的）。
 */
export function statusCountsOf(row: TrackerRow): TrackerStatusCounts {
  return {
    discarded: row.inDiscard,
    inHand: row.inYourHand,
    unknown: Math.max(0, row.inPlay - row.inDiscard - row.inYourHand),
  };
}

/** 从一份视角算出记牌结论 */
export function trackDeck(view: PlayerView, registry: CardRegistry): TrackerMemory {
  const inPlay = inPlayDeck(view.config, registry);
  const discarded = countBy(view.discardPile.map((card) => card.title));
  const inHand = countBy(view.yourHand.map((card) => card.title));
  const located = countBy(view.knownTop.map((card) => card.title));
  const region = weightedBy(view.knownInDraw);
  const knownInHand = new Map<string, number>();
  for (const entry of view.knownInHand) {
    const key = entry.player + '\u0000' + entry.title;
    knownInHand.set(key, (knownInHand.get(key) ?? 0) + entry.count);
  }

  /**
   * ★ **致命牌（异常）此刻公开在谁手里。**
   *
   * 抽到致命牌 → 它进抽牌者的手（`reducer.ts` 抽牌分支），并设一个
   * `useRescue` / `insertLethal` 抉择；`project` 把牌名放进
   * `pending.revealedTitle`。对**别人**来说这是公开信息：那张牌此刻就在
   * 他的手上（而且他正在濒死）。
   */
  const revealedPlayer =
    view.pending !== null && view.pending.revealedTitle !== null && view.pending.player !== view.you
      ? view.pending.player
      : null;
  const revealedTitle = revealedPlayer === null ? null : (view.pending?.revealedTitle ?? null);

  const otherPlayers: TrackerPlayer[] = view.players
    .filter((player) => player.id !== view.you)
    .map((player) => {
      let knownSlots = 0;
      for (const entry of view.knownInHand) if (entry.player === player.id) knownSlots += entry.count;
      // 濒死那张公开的致命牌也占掉他一个"已知槽位"，不再算进未知手牌位
      if (revealedPlayer === player.id) knownSlots += 1;
      return {
        id: player.id,
        name: player.name,
        handCount: player.handCount,
        knownSlots,
      };
    });
  const otherHands = otherPlayers.reduce((sum, player) => sum + player.handCount, 0);
  const knownInDrawTotal =
    view.knownTop.length + view.knownInDraw.reduce((sum, entry) => sum + entry.count, 0);
  const unknownDrawSlots = Math.max(0, view.drawPileCount - knownInDrawTotal);
  const unknownHandSlots = otherPlayers.reduce(
    (sum, player) => sum + Math.max(0, player.handCount - player.knownSlots),
    0,
  );
  const hiddenSlots = unknownDrawSlots + unknownHandSlots;

  const rows: TrackerRow[] = [];
  for (const def of registry.cards) {
    const title = def.id;
    // ★ 区域硬约束：这张牌永远不会在某人手里（异常牌就是如此）。
    //   优先读 trait；isLethal 兜底，兼容没打这条 trait 的旧包。
    const neverInHand = def.traits.neverInHand === true || isLethal(def);
    const total = inPlay[title] ?? 0;
    const inDiscard = discarded[title] ?? 0;
    const inYourHand = inHand[title] ?? 0;
    const locatedInDraw = located[title] ?? 0;
    const regionInDraw = region[title] ?? 0;
    let knownHands = 0;
    const expectedInHands: Record<string, number> = {};
    for (const player of otherPlayers) {
      let known = knownInHand.get(player.id + '\u0000' + title) ?? 0;
      // 濒死那位手里公开的致命牌：算作"已知在他手里"
      if (revealedTitle === title && revealedPlayer === player.id) known += 1;
      knownHands += known;
      expectedInHands[player.id] = known;
    }
    if (total === 0 && inDiscard === 0 && inYourHand === 0) continue;
    const unlocated = Math.max(
      0,
      total - inDiscard - inYourHand - locatedInDraw - regionInDraw - knownHands,
    );
    const rawDraw = hiddenSlots > 0 ? (unlocated * unknownDrawSlots) / hiddenSlots : 0;
    // ★ 这类牌的未定位份额**全部落抽牌堆**，不往活人手上摊（唯一例外是上面那张公开的）
    if (!neverInHand) {
      for (const player of otherPlayers) {
        const share =
          hiddenSlots > 0
            ? (unlocated * Math.max(0, player.handCount - player.knownSlots)) / hiddenSlots
            : 0;
        expectedInHands[player.id] = (expectedInHands[player.id] ?? 0) + share;
      }
    }
    rows.push({
      title,
      name: cardName(def),
      inPlay: total,
      inDiscard,
      inYourHand,
      locatedInDraw,
      regionInDraw,
      unlocated,
      expectedInDraw: locatedInDraw + regionInDraw + (neverInHand ? unlocated : rawDraw),
      expectedInHands,
    });
  }

  const knownTop: TrackerKnown[] = [...view.knownTop]
    .sort((a, b) => a.indexFromTop - b.indexFromTop)
    .map((card) => {
      const def = registry.get(card.title);
      return {
        indexFromTop: card.indexFromTop,
        title: card.title,
        name: def === undefined ? String(card.title) : cardName(def),
      };
    });

  return {
    drawPileCount: view.drawPileCount,
    otherHands,
    knownInDrawTotal,
    hiddenSlots,
    otherPlayers,
    rows,
    knownTop,
  };
}

/** 抽牌堆的"自底向上"牌面列表：有牌名 = 确定是这张，`null` = 未知 */
export function drawPileFaces(view: PlayerView): (CardTitle | null)[] {
  const count = Number.isFinite(view.drawPileCount) ? Math.max(0, Math.floor(view.drawPileCount)) : 0;
  const out: (CardTitle | null)[] = Array.from({ length: count }, () => null);
  for (const card of view.knownTop) {
    const arrayIndex = count - 1 - card.indexFromTop;
    if (arrayIndex >= 0 && arrayIndex < out.length) out[arrayIndex] = card.title;
  }
  return out;
}

function countBy(titles: readonly CardTitle[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const title of titles) out[title] = (out[title] ?? 0) + 1;
  return out;
}

function weightedBy(entries: readonly { readonly title: CardTitle; readonly count: number }[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const entry of entries) out[entry.title] = (out[entry.title] ?? 0) + entry.count;
  return out;
}
